import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ExternalLink,
  Loader2,
  Shield,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useAdminImpersonation } from "@/hooks/useAdminImpersonation";
import { Role } from "@/types/user";
import {
  useTwoFactorStatus,
  useRequestEnable2fa,
  useConfirmEnable2fa,
  useDisable2fa,
  useAccountUrl,
} from "../hooks/useTwoFactor";

type TwoFactorState =
  | "IDLE"
  | "SENDING_OTP"
  | "OTP_SENT"
  | "VERIFYING"
  | "ENABLED";

const RESEND_COOLDOWN = 60;

export const SecurityTab: React.FC = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const { isImpersonating } = useAdminImpersonation();
  const userRole = userProfile?.role;
  const canUse2fa =
    userRole === Role.FRONT_DESK ||
    userRole === Role.HOTEL_ADMIN ||
    userRole === Role.TENANT_ADMIN;
  const { data: twoFactorStatus, isLoading: isStatusLoading } =
    useTwoFactorStatus(canUse2fa);
  const requestEnable = useRequestEnable2fa();
  const confirmEnable = useConfirmEnable2fa();
  const disable2fa = useDisable2fa();
  const { data: accountUrlData } = useAccountUrl(canUse2fa);

  const [state, setState] = useState<TwoFactorState>("IDLE");
  const [otpCode, setOtpCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [showDisableDialog, setShowDisableDialog] = useState(false);

  const phone = userProfile?.profile?.phone;

  useEffect(() => {
    if (twoFactorStatus?.enabled) {
      setState("ENABLED");
    } else {
      setState("IDLE");
    }
  }, [twoFactorStatus?.enabled]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleRequestEnable = useCallback(async () => {
    if (isImpersonating) {
      return;
    }

    setState("SENDING_OTP");
    try {
      await requestEnable.mutateAsync();
      setState("OTP_SENT");
      setCountdown(RESEND_COOLDOWN);
      setOtpCode("");
    } catch {
      setState("IDLE");
    }
  }, [isImpersonating, requestEnable]);

  const handleVerify = useCallback(async () => {
    if (otpCode.length !== 6 || isImpersonating) return;
    setState("VERIFYING");
    try {
      await confirmEnable.mutateAsync({ code: otpCode });
      setState("ENABLED");
      setOtpCode("");
    } catch {
      setState("OTP_SENT");
    }
  }, [confirmEnable, isImpersonating, otpCode]);

  const handleDisable = useCallback(async () => {
    if (isImpersonating) {
      return;
    }

    try {
      await disable2fa.mutateAsync();
      setShowDisableDialog(false);
      setState("IDLE");
    } catch {
      // Error handled by mutation onError / BaseService toast
    }
  }, [disable2fa, isImpersonating]);

  const isEnabled = state === "ENABLED";

  return (
    <div className="flex flex-col gap-6">
      {/* Two-Factor Authentication — hidden for STAFF role */}
      {canUse2fa && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="size-5 text-muted-foreground" />
              <CardTitle>{t("account.security.twoFactor.title")}</CardTitle>
            </div>
            <CardDescription>
              {t("account.security.twoFactor.description")}
            </CardDescription>
            {isImpersonating ? (
              <p className="text-sm text-muted-foreground">
                {t("account.impersonationBlocked")}
              </p>
            ) : null}
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Status */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">
                {t("account.security.twoFactor.status")}:
              </span>
              {isStatusLoading && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {!isStatusLoading && isEnabled && (
                <Badge
                  variant="outline"
                  className="border-green-200 bg-green-50 text-green-700"
                >
                  <ShieldCheck className="size-3 mr-1" />
                  {t("account.security.twoFactor.enabled")}
                </Badge>
              )}
              {!isStatusLoading && !isEnabled && (
                <Badge
                  variant="outline"
                  className="border-orange-200 bg-orange-50 text-orange-700"
                >
                  <ShieldOff className="size-3 mr-1" />
                  {t("account.security.twoFactor.disabled")}
                </Badge>
              )}
            </div>

            {/* Phone */}
            {phone && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-muted-foreground">
                  {t("account.security.twoFactor.phone")}:
                </span>
                <span className="text-sm">{phone}</span>
              </div>
            )}

            {/* Enable/Disable Button */}
            {state === "IDLE" && (
              <Button
                onClick={handleRequestEnable}
                disabled={requestEnable.isPending || isImpersonating}
                className="w-fit"
              >
                {t("account.security.twoFactor.enableButton")}
              </Button>
            )}

            {state === "ENABLED" && (
              <Button
                variant="destructive"
                onClick={() => setShowDisableDialog(true)}
                disabled={isImpersonating}
                className="w-fit"
              >
                {t("account.security.twoFactor.disableButton")}
              </Button>
            )}

            {/* OTP Input (when enabling) */}
            {(state === "OTP_SENT" ||
              state === "SENDING_OTP" ||
              state === "VERIFYING") && (
              <div className="flex flex-col gap-4 rounded-lg border p-4">
                <label
                  htmlFor="otp-code"
                  className="text-sm font-medium text-muted-foreground"
                >
                  {t("account.security.twoFactor.enterCode")}
                </label>
                <div className="flex items-center gap-3">
                  <Input
                    id="otp-code"
                    value={otpCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setOtpCode(val);
                    }}
                    placeholder="000000"
                    className="h-12 w-40 text-center text-lg tracking-widest"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleVerify}
                    disabled={
                      otpCode.length !== 6 ||
                      state === "VERIFYING" ||
                      confirmEnable.isPending ||
                      isImpersonating
                    }
                  >
                    {t("account.security.twoFactor.verifyButton")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRequestEnable}
                    disabled={
                      countdown > 0 ||
                      requestEnable.isPending ||
                      isImpersonating
                    }
                  >
                    {countdown > 0
                      ? t("account.security.twoFactor.resendCountdown", {
                          seconds: countdown,
                        })
                      : t("account.security.twoFactor.resendCode")}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Advanced Security Settings — requires FD/HA/TA role */}
      {canUse2fa && (
        <Card>
          <CardHeader>
            <CardTitle>{t("account.security.advanced.title")}</CardTitle>
            <CardDescription>
              {t("account.security.advanced.description")}
            </CardDescription>
            {isImpersonating ? (
              <p className="text-sm text-muted-foreground">
                {t("account.impersonationBlocked")}
              </p>
            ) : null}
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="w-fit gap-2"
              disabled={isImpersonating}
              asChild={!isImpersonating}
            >
              {isImpersonating ? (
                <span>
                  <ExternalLink className="size-4" />
                  {t("account.security.advanced.openButton")}
                </span>
              ) : (
                <a
                  href={accountUrlData?.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="size-4" />
                  {t("account.security.advanced.openButton")}
                </a>
              )}
            </Button>
            <p className="text-xs text-muted-foreground">
              {t("account.security.advanced.opensInNewTab")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Disable 2FA Confirmation Dialog */}
      {canUse2fa && (
        <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("account.security.twoFactor.disableConfirmTitle")}
              </DialogTitle>
              <DialogDescription>
                {t("account.security.twoFactor.disableConfirmDescription")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDisableDialog(false)}
              >
                {t("account.security.twoFactor.cancelButton")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisable}
                disabled={disable2fa.isPending || isImpersonating}
              >
                {t("account.security.twoFactor.disableConfirmButton")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
